import CuesheetRowActions from 'flux/actions/cuesheet/CuesheetRowActions';
import CuesheetDataHelper from 'flux/stores/cuesheet/CuesheetDataHelper';
import { Map, fromJS } from 'immutable';
import { findRowPosition } from './CuesheetCursorHelper';

/**
 * Transient per-row flags that live on the row specs (not in the rowMap), so
 * they are lost whenever the rows list is rebuilt from a new order and must be
 * captured and re-applied by rowId. See _captureTransientRowFlags.
 */
const TRANSIENT_ROW_FLAGS = ['rowSelected', 'editing', 'tracked'];

/**
 * CuesheetRealTime Mixin
 *
 */
export default {
  // eslint-disable-next-line no-unused-vars
  onEventMutatedRealTime(teamId, eventId, changes) {
    if (eventId !== this.getInState('eventId')) return;
    this.setStateAndTrigger(this.touchAllRows());
  },

  onShowMutatedRealTime(showId, changes) {
    if (this.getInState('showId') !== showId) return;

    let next = this.getState();

    const mutatedKeys = Object.keys(changes);

    if (mutatedKeys.indexOf('displayItemDescriptions') >= 0) {
      next = this.touchAllRows(next);
      CuesheetRowActions.bustRowHeightCache();
    }

    this.setStateAndTrigger(next.mergeIn(['show'], Map(changes)));
  },

  onShowRecountedRealTime(showId, payload, responsePayload) {
    if (this.getInState('showId') !== showId) return;

    let next = this.getState().updateIn(
      ['rowMap', responsePayload[0]._id, 'rows'],
      (rows) => {
        /**
         * Use recounted row numbers from responsePayload to update rowMap
         * row numbers.
         */
        responsePayload[0].rows.map((row) => {
          rows = rows.setIn([row._id, 'item', 'number'], row.item[0].number);
          rows = rows.setIn(
            [row._id, 'item', 'numberAdjusted'],
            row.item[0].number,
          );
        });

        /**
         * Return updated rows map
         */
        return rows;
      },
    );

    next = this.touchAllRows(next);

    this.setStateAndTrigger(next);
  },

  onShowResetAllStartTimesRealTime(showId, payload, responsePayload) {
    if (this.getInState('showId') !== showId) return;

    let next = this.getState().updateIn(
      ['rowMap', responsePayload[0]._id, 'rows'],
      (rows) => {
        /**
         * Use recounted row numbers from responsePayload to update rowMap
         * row numbers.
         */
        responsePayload[0].rows.map((row) => {
          rows = rows.setIn([row._id, 'item', 'startOn'], row.item[0].startOn);
        });

        /**
         * Return updated rows map
         */
        return rows;
      },
    );

    next = this.touchAllRows(next);
    this.recalculateTimeFieldsAndTrigger(next);
  },

  onItemSetListItemSettingsUpdatedRealTime(showId, payload) {
    if (this.getInState('showId') !== showId) return;

    this.updateItemColumnSettingsTransient(payload.changes, payload.fieldName);
  },

  onItemSetListColumnCreatedRealTime(showId, payload, responsePayload) {
    if (this.getInState('showId') !== showId) return;

    this.addColumnTransient(responsePayload.column);
  },

  // eslint-disable-next-line no-unused-vars
  onItemSetListColumnDeletedRealTime(showId, payload, responsePayload) {
    if (this.getInState('showId') !== showId) return;

    this.deleteColumnTransient(payload.columnId);
  },

  // eslint-disable-next-line no-unused-vars
  onItemSetListColumnMovedRealTime(showId, payload, responsePayload) {
    if (this.getInState('showId') !== showId) return;

    this.moveColumnTransient(payload.columnId, payload.afterColumnId);
  },

  onItemRowRestoredRealTime(params, payload, responsePayload) {
    const { showId } = params;

    if (this.getInState('showId') !== showId) return;
    this.restoreItemRow(responsePayload);
  },

  onItemRowMultipleCellsMutatedRealTime(showId, payload, responsePayload) {
    if (this.getInState('showId') !== showId) return;

    this.saveMultipleDataCellChanges(fromJS(responsePayload), false, true);
  },

  // eslint-disable-next-line no-unused-vars
  onItemRowNoteMutatedRealTime(showId, payload, responsePayload) {
    if (this.getInState('showId') !== showId) return;

    const rowIndex = this.findRowPosition(payload.itemSetId, payload.rowId);

    const nextState = this.saveNoteCellChangesTransient(
      rowIndex,
      payload.userId,
      fromJS(payload.changes),
    );

    this.setStateAndTrigger(nextState);
  },

  _deleteProof(showId, itemSetId, rowId) {
    if (this.getInState('showId') !== showId) return;

    const rowIndex = this.findRowPosition(itemSetId, rowId);

    const path = [
      'rowMap',
      ...this.getInState(['rows', rowIndex, 'path']),
      'proof',
    ];

    const nextState = this.getState().withMutations((state) => {
      state.deleteIn(path);
      this.touchRow(rowIndex, state);
    });

    this.setStateAndTrigger(this.getClearedScrollIndexState(nextState));
    CuesheetRowActions.bustRowHeightCache(rowId);
  },

  _setProof(showId, itemSetId, rowId, proof, merge) {
    if (this.getInState('showId') !== showId) return;

    const rowIndex = this.findRowPosition(itemSetId, rowId);

    const path = this.getInState(['rows', rowIndex, 'path']);

    const nextState = this.getState().withMutations((state) => {
      state[merge ? 'mergeIn' : 'setIn'](
        ['rowMap'].concat(path).concat('proof'),
        fromJS(proof),
      );
      this.touchRow(rowIndex, state);
    });

    this.setStateAndTrigger(this.getClearedScrollIndexState(nextState));
    CuesheetRowActions.bustRowHeightCache(rowId);
  },

  onItemRowProofAddedRealTime(showId, payload, { itemSetId, rowId, proof }) {
    this._setProof(showId, itemSetId, rowId, proof);
  },

  onItemRowProofRemovedRealTime(showId, { itemSetId, rowId }) {
    this._deleteProof(showId, itemSetId, rowId);
  },

  onItemRowProofNoteUpdatedRealTime(
    showId,
    payload,
    { itemSetId, rowId, proof },
  ) {
    this._setProof(showId, itemSetId, rowId, proof, true);
  },

  onShowProofsClearedRealTime(showId) {
    if (this.getInState('showId') !== showId) return;

    const nextState = this.getState().withMutations((state) => {
      state.get('rows').forEach((row) => {
        const path = row.get('path');
        state.setIn(['rowMap'].concat(path).concat('proof'), undefined);
      });

      this.touchAllRows(state);
    });

    this.setStateAndTrigger(this.getClearedScrollIndexState(nextState));
    CuesheetRowActions.bustRowHeightCache();
  },

  onItemRowsAddedRealTime(showId, payload, responsePayload) {
    if (this.getInState('showId') !== showId) return;

    const { itemSetId, afterRowId, rows } = responsePayload;

    this.addItemRowsTransient(itemSetId, afterRowId, rows);
    this._refreshGlobalElementRowAssociations();
  },

  // eslint-disable-next-line no-unused-vars
  onRemoveGlobalElementReferenceRealTime(showId, payload, responsePayload) {
    if (this.getInState('showId') !== showId) return;
    this._refreshGlobalElementRowAssociations();
  },

  // eslint-disable-next-line no-unused-vars
  onRemoveGlobalElementReferencesRealTime(showId, payload, responsePayload) {
    if (this.getInState('showId') !== showId) return;
    this._refreshGlobalElementRowAssociations();
  },

  onPublishLatestGlobalElementRowChangesToShowRowRealTime(
    showId,
    payload,
    responsePayload,
  ) {
    if (this.getInState('showId') !== showId) return;

    this._retrieveLatestGlobalElementVersionTransient(responsePayload);
  },

  onPublishGlobalElementChangesRealTime(params, row) {
    if (this.getInState('showId') !== params.showId) return;
    this._updateRow(row);
  },

  onRowUpdatedRealTime(showId, payload, responsePayload) {
    if (this.getInState('showId') !== showId) return;
    this._updateRow(responsePayload);
  },

  onPublishShowRowChangesToGlobalElementRowRealTime(
    showId,
    payload,
    responsePayload, // eslint-disable-line no-unused-vars
  ) {
    if (this.getInState('showId') !== showId) return;
    this._updateRow(payload);
  },

  onCreatedGlobalElementFromShowRowRealTime(params, payload, responsePayload) {
    if (this.getInState('showId') !== params.showId) return;

    let lastRow = this.getInState('rows').last(),
      lastRowId = lastRow.get('path')[2],
      itemSetId = lastRow.get('path')[0];

    this.addItemRowsTransient(itemSetId, lastRowId, responsePayload);
  },

  _updateRow(row) {
    let next = this.getState();

    /**
     * In support of https://github.com/SHOFLO/Issues/issues/5202
     *
     * When we have unfilteredRows key in state, it means the Global Element view has a search or label filter, and
     * what is in the 'rows' key is a filtered set of rows. In order to get the row index to match up with the correct row
     * in realtime scenarios (sometimes the user receiving the realtime update has a different filter) we reset
     * 'rows' to what is in 'unfilteredRows'. We only do this for realtime & transient changes.
     *
     * Then further down we refilter the rows with any filters there are in state.
     */
    if (next.get('unfilteredRows')) {
      const rows = next.get('rows');
      const unfilteredRows = this.updateUnfilteredRowsWithRowFields(
        rows,
        next.get('unfilteredRows'),
      );

      next = next.set('unfilteredRows', unfilteredRows);
      next = next.set('rows', unfilteredRows);
    }

    const itemSetId = next.getIn(['rows', 0, 'path'])[0],
      rowIndex = this.findRowPosition(itemSetId, row._id, next),
      rowMapPath = next.getIn(['rows', rowIndex, 'path']),
      globalElementShowItems = next.get('globalElementShowItems');

    let globalElementRowIdLookup = {};
    globalElementShowItems.forEach((ge) => {
      globalElementRowIdLookup[ge.row] = ge;
    });

    next = next
      .setIn(['rows', rowIndex, 'touch'], new Date().getTime())
      .updateIn(['rowMap'].concat(rowMapPath), () => {
        let item = row.item[0];

        item.titleView = row.titleRow;
        item.labels = row.labels;
        item.globalElement = globalElementRowIdLookup[row._id]
          ? globalElementRowIdLookup[row._id]
          : false;

        return fromJS(item);
      });

    /**
     * In support of https://github.com/SHOFLO/Issues/issues/5202
     *
     * Refilter the rows in case they were changed above as part of a realtime transient change
     */
    next = this._justFilterRows(next);

    next = this.recalculateTimeFields(next);

    this.setStateAndTrigger(next);

    CuesheetRowActions.bustRowHeightCache(row._id);
  },

  onGlobalElementItemRowsAddedRealTime(showId, payload, responsePayload) {
    if (this.getInState('showId') !== showId) return;

    let { itemSetId, afterRowId, rows } = responsePayload;

    this.addItemRowsTransient(itemSetId, afterRowId, rows);

    this._refreshGlobalElementRowAssociations();
  },

  itemRowDeletedRealTime(showId, payload) {
    if (this.getInState('showId') !== showId) return;

    const { itemSetId, rowId } = payload;

    this.deleteRowsTransient(itemSetId, [rowId]);
  },

  onItemRowsDeletedRealTime(showId, { itemSetId, rowIds }) {
    if (this.getInState('showId') !== showId) return;

    this.deleteRowsTransient(itemSetId, rowIds);
  },

  _updateRowOrder(showId, itemSetId, newRowOrder) {
    if (this.getInState('showId') !== showId) return;
    /**
     * While this client has row moves of its own buffered or in flight for
     * this itemSet, an incoming row order was computed before those moves
     * landed; applying it would visually revert them. Skip it — the response
     * to our own move re-syncs us to the authoritative order once we're idle.
     */
    if (this._hasPendingRowMoves(itemSetId)) return;

    const previous = this.getState();

    /**
     * Transient per-row flags (selection, in-progress edit, on-air tracking)
     * live on the row specs, not in the rowMap that newRowIndex is rebuilt
     * from, so the rebuild drops them. Capture by rowId and re-apply by rowId
     * below — re-syncing to a new order (our own move settling, or a remote
     * reorder) must not clear the user's selection, edit, or tracked row.
     */
    const transientFlags = this._captureTransientRowFlags(previous);

    const newRowIndex = CuesheetDataHelper.createRowIndexFromRowOrder(
      itemSetId,
      newRowOrder,
      this.getInState('rowMap'),
    );

    let next = this.recalculateCellSelectionRange(
      previous.set('rows', newRowIndex),
    );
    next = this._applyTransientRowFlags(next, transientFlags);
    next = this._setTrackableUsersInRows(next);

    this.recalculateTimeFieldsAndTrigger(next);
  },

  /**
   * Snapshot the transient per-row flags (TRANSIENT_ROW_FLAGS) keyed by rowId,
   * so they survive a rebuild that reconstructs row specs from the rowMap.
   * Keyed by rowId, not index, because the rebuild reorders the rows.
   */
  _captureTransientRowFlags(state) {
    const captured = {};

    state.get('rows').forEach((row) => {
      const rowId = row.get('path')[2];

      TRANSIENT_ROW_FLAGS.forEach((flag) => {
        const value = row.get(flag);
        if (!value) return;
        captured[flag] = captured[flag] || {};
        captured[flag][rowId] = value;
      });
    });

    return captured;
  },

  /**
   * Re-apply captured transient flags to the rebuilt rows by rowId. Matching
   * by id means a row that no longer exists in the new order is simply skipped
   * — no index arithmetic that could land a flag on the wrong row.
   */
  _applyTransientRowFlags(state, captured) {
    const flags = Object.keys(captured);
    if (!flags.length) return state;

    return state.update('rows', (rows) =>
      rows.map((row) => {
        const rowId = row.get('path')[2];
        return flags.reduce((acc, flag) => {
          const value = captured[flag][rowId];
          return value === undefined ? acc : acc.set(flag, value);
        }, row);
      }),
    );
  },

  onItemRowMovedFromSourceToDestinationRealTime(
    params,
    payload,
    responsePayload, // eslint-disable-line no-unused-vars
  ) {
    if (this.getInState('showId') !== params.showId) return;
    //Same deferral as the other realtime reorder handlers: a remote drag-drop
    //computed before our local moves landed would revert our optimistic order.
    if (this._hasPendingRowMoves(params.itemSetId)) return;

    let next = this.moveFromToTransient(
      params.showId,
      params.itemSetId,
      payload.source,
      payload.target,
    );

    next = this.updateUnfilteredRows(next.get('rows'), next);
    next = this.recalculateTimeFields(next);

    this.setStateAndTrigger(next);
  },

  onItemRowsMovedRealTime(showId, { itemSetId }, newRowOrder) {
    this._updateRowOrder(showId, itemSetId, newRowOrder);
  },

  onItemRowOrderUpdatedRealTime(showId, { itemSetId }, newRowOrder) {
    this._updateRowOrder(showId, itemSetId, newRowOrder);
  },

  onItemSetListColumnRestoredRealTime(params, payload, result) {
    this.addColumnTransient(result.column);
  },

  onColumnUpdatedRealTime(showId, payload) {
    if (this.getInState('showId') !== showId) return;

    const { columnId } = payload;

    const next = this.getState()
      .updateIn(['columns', columnId], (col) => col.merge(payload.changes))
      .updateIn(['fullColumns', columnId], (col) => col.merge(payload.changes));

    CuesheetRowActions.bustRowHeightCache();

    this.setStateAndTrigger(next);
  },

  onSetColumnOrderFromUserRealTime(showId, payload, responsePayload) {
    if (this.getInState('showId') !== showId) return;

    const prevColumnOrder = this.getInState('columnOrder');
    const itemSetList = responsePayload;
    const fullColumnOrder = CuesheetDataHelper.getFullColumnOrder(
      itemSetList,
      payload.userId,
    );
    //Remove any that are not in the user's column order at all (i.e. they are hidden)
    const columnOrder = CuesheetDataHelper.getColumnOrder(
      itemSetList,
      payload.userId,
    ).filter((columnId) => prevColumnOrder.indexOf(columnId) !== -1);
    const next = this.getState()
      .set(
        'columns',
        CuesheetDataHelper.getSortedColumns(itemSetList.columns, columnOrder),
      )
      .set('columnOrder', columnOrder)
      .set(
        'fullColumns',
        CuesheetDataHelper.getSortedColumns(
          itemSetList.columns,
          fullColumnOrder,
        ),
      )
      .set('fullColumnOrder', fullColumnOrder);

    CuesheetRowActions.bustRowHeightCache();

    this.setStateAndTrigger(next);
  },

  onItemRowMutatedRealTime(showId, payload) {
    if (this.getInState('showId') !== showId) return;

    const { itemSetId, rowId } = payload;

    let next = this.getState();

    /**
     * In support of https://github.com/SHOFLO/Issues/issues/5202
     *
     * When we have unfilteredRows key in state, it means the Global Element view has a search or label filter, and
     * what is in the 'rows' key is a filtered set of rows. In order to get the row index to match up with the correct row
     * in realtime scenarios (sometimes the user receiving the realtime update has a different filter) we reset
     * 'rows' to what is in 'unfilteredRows'. We only do this for realtime & transient changes.
     *
     * Then further down we refilter the rows with any filters there are in state.
     */
    let needsRefilteringCleanup = false;
    if (next.get('unfilteredRows')) {
      needsRefilteringCleanup = true;
      const rows = next.get('rows');
      const unfilteredRows = this.updateUnfilteredRowsWithRowFields(
        rows,
        next.get('unfilteredRows'),
      );

      next = next.set('unfilteredRows', unfilteredRows);
      next = next.set('rows', unfilteredRows);
    }

    const rowIndex = findRowPosition(itemSetId, rowId, next);

    //1. Update the item number
    next = this.saveItemFieldChangesTransient(
      rowIndex,
      fromJS(payload.changes),
      next,
    );

    //2. Update all rows if auto count is on
    if (payload.changes.number && this.getInState(['show', 'autoCountCues'])) {
      next = this.touchAllRows(next);
    }

    CuesheetRowActions.bustRowHeightCache(rowId);

    /**
     * In support of https://github.com/SHOFLO/Issues/issues/5202
     *
     * Refilter the rows in case they were changed above as part of a realtime transient change
     */
    if (needsRefilteringCleanup) {
      next = this._justFilterRows(next);
    }

    this.setStateAndTrigger(next);
  },

  onItemSetListItemRowSettingsUpdatedRealTime(showId, payload) {
    let nextState = this.getState();

    if (nextState.get('showId') !== showId) return;

    const { itemSetId, changes } = payload;
    const { rowId } = payload;
    let { rowIds } = payload;

    rowIds = rowIds || [rowId];

    rowIds.forEach((id) => {
      const path = CuesheetDataHelper.getRowPath(itemSetId, id);
      const rowIndex = this.findRowPosition(itemSetId, id);
      const settingPath = ['settings', 'highlightClass'];

      if (this.getItemRowLockStatusWithAlert(rowIndex)) return;

      nextState = nextState
        .setIn(
          ['rowMap'].concat(path).concat(settingPath),
          changes.highlightClass,
        )
        .setIn(['rows', rowIndex, 'touch'], new Date().getTime());
    });

    this.setStateAndTrigger(nextState);
  },

  onItemRowPresentationUpdatedRealTime(showId, payload) {
    if (this.getInState('showId') !== showId) return;

    const { itemSetId, rowId, changes } = payload;

    const rowIndex = this.findRowPosition(itemSetId, rowId);

    if (changes.titleRow === false) {
      this.convertToItemRowTransient(rowIndex);
    } else if (changes.titleRow === true) {
      this.convertToTitleRowTransient(rowIndex);
    }
  },
};
